// ---------------------------------------------------------------------------
// Survive on BCH — Hustle Mode Engine (pure functions)
//
// All computations operate on SIMULATED values only. Randomness (chargebacks,
// Lightning routing failures) is derived deterministically from the customer +
// method so a given decision always resolves the same way in a session.
// ---------------------------------------------------------------------------

import {
  BCH_ACCEPT_FEE_USD,
  CARD_CHARGEBACK_ODDS,
  CARD_CHARGEBACK_ODDS_RISKY,
  CARD_FEE_FLAT_USD,
  CARD_FEE_RATE,
  LIGHTNING_ACCEPT_FEE_USD,
  LIGHTNING_FAIL_ODDS,
  STARTING_REPUTATION,
} from "./data";
import type {
  AcceptMethod,
  Customer,
  Flexibility,
  HustleResult,
  HustleSale,
  RoundConditions,
} from "./types";

/** All the concrete accept choices offered for a customer (never "decline" here). */
export const ACCEPT_METHODS: AcceptMethod[] = [
  "bch",
  "btc-lightning",
  "btc-onchain",
  "card",
];

/** Human-friendly labels for methods. */
export function methodLabel(method: AcceptMethod): string {
  switch (method) {
    case "bch":
      return "BCH";
    case "btc-lightning":
      return "BTC Lightning";
    case "btc-onchain":
      return "BTC on-chain";
    case "card":
      return "Card";
    case "decline":
      return "Decline";
  }
}

/** Whether a given method can even be offered to this customer. */
export function methodAvailable(method: AcceptMethod, customer: Customer): boolean {
  if (method === "btc-lightning") return customer.lightningCapable;
  return true;
}

/**
 * Deterministic pseudo-random in [0,1) from a string seed. Small, stable hash
 * so the same customer+method always yields the same roll within a session.
 */
function seededRoll(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Map to [0,1)
  return ((h >>> 0) % 100000) / 100000;
}

/** Does a completed card sale for this customer get charged back later? */
export function cardChargesBack(customer: Customer): boolean {
  const odds = customer.cardRisky ? CARD_CHARGEBACK_ODDS_RISKY : CARD_CHARGEBACK_ODDS;
  return seededRoll(`${customer.id}:chargeback`) < odds;
}

/** Does a Lightning attempt fail to route for this customer? */
export function lightningFails(customer: Customer): boolean {
  return seededRoll(`${customer.id}:lnfail`) < LIGHTNING_FAIL_ODDS;
}

/** A customer's flexibility, defaulting to "flexible". */
export function flexibilityOf(customer: Customer): Flexibility {
  return customer.flexibility ?? "flexible";
}

/**
 * The set of methods a BINDING customer will accept (their `prefers` plus any
 * extra `insistsOn` methods). Only meaningful for binding customers; flexible
 * and firm customers will accept any method (see `customerAccepts`).
 */
export function acceptedMethods(customer: Customer): AcceptMethod[] {
  const set = new Set<AcceptMethod>(customer.insistsOn ?? []);
  set.add(customer.prefers);
  return [...set];
}

/**
 * Will this customer complete the sale if offered the given method?
 *  - flexible / firm: yes for any real method (they may grumble, but they pay).
 *  - binding: only methods in their accepted set; anything else = walk out.
 */
export function customerAccepts(customer: Customer, method: AcceptMethod): boolean {
  if (method === "decline") return false;
  if (flexibilityOf(customer) === "binding") {
    return acceptedMethods(customer).includes(method);
  }
  return true;
}

/**
 * Would offering this method require "steering" the customer away from their
 * preference? (Used for outcome flavor + reputation.)
 */
export function isSteer(customer: Customer, method: AcceptMethod): boolean {
  return method !== customer.prefers && method !== "decline";
}

/** The simulated fee (USD) for accepting a sale via a method under conditions. */
export function acceptFee(
  method: AcceptMethod,
  priceUsd: number,
  conditions: RoundConditions,
): number {
  switch (method) {
    case "bch":
      return round2(BCH_ACCEPT_FEE_USD);
    case "btc-lightning":
      return round2(LIGHTNING_ACCEPT_FEE_USD);
    case "btc-onchain":
      return round2(conditions.btcOnchainFeeUsd);
    case "card":
      return round2(priceUsd * CARD_FEE_RATE + CARD_FEE_FLAT_USD);
    case "decline":
      return 0;
  }
}

/** Is this round's on-chain BTC fee a congestion spike? */
export function feeIsSpiked(method: AcceptMethod, conditions: RoundConditions): boolean {
  if (method !== "btc-onchain") return false;
  return conditions.btcCongested;
}

/**
 * Resolve accepting a customer's sale via a chosen method. Pure — no mutation.
 * Encodes the whole point of the mode: the tradeoffs differ sharply by method.
 */
export function resolveSale(
  customer: Customer,
  method: AcceptMethod,
  conditions: RoundConditions,
): HustleSale {
  const base: Omit<HustleSale, "method" | "completed" | "grossUsd" | "feeUsd" | "feeSpiked" | "chargebackUsd" | "netUsd" | "reputationDelta" | "outcome"> = {
    round: customer.round,
    customerName: customer.name,
    item: customer.item,
    priceUsd: customer.priceUsd,
    // Overridden to true only on a completed non-BCH-preferring → BCH sale.
    converted: false,
  };

  // Reputation baseline: matching the customer's preference feels smooth.
  const matchesPref = method === customer.prefers;

  if (method === "decline") {
    return {
      ...base,
      method,
      completed: false,
      grossUsd: 0,
      feeUsd: 0,
      feeSpiked: false,
      chargebackUsd: 0,
      netUsd: 0,
      reputationDelta: -8,
      outcome: `You turned ${customer.name} away. No sale, and they leave unhappy.`,
    };
  }

  // Every customer is bound to their choice: offer a method they won't take
  // and they walk out (e.g. a Bitcoin maximalist who won't touch BCH, or a
  // card-only shopper who doesn't get crypto yet).
  if (!customerAccepts(customer, method)) {
    const wanted = acceptedMethods(customer)
      .map((m) => methodLabel(m))
      .join(" or ");
    return {
      ...base,
      method,
      completed: false,
      grossUsd: 0,
      feeUsd: 0,
      feeSpiked: false,
      chargebackUsd: 0,
      netUsd: 0,
      reputationDelta: -6,
      outcome: `${customer.name} insists on paying with ${wanted} — "${methodLabel(
        method,
      )}? No thanks." They refuse your terms and walk out. No sale.`,
    };
  }

  const price = customer.priceUsd;
  const fee = acceptFee(method, price, conditions);
  const spiked = feeIsSpiked(method, conditions);

  // Lightning can fail to route for capable customers.
  if (method === "btc-lightning" && lightningFails(customer)) {
    return {
      ...base,
      method,
      completed: false,
      grossUsd: 0,
      feeUsd: 0,
      feeSpiked: false,
      chargebackUsd: 0,
      netUsd: 0,
      reputationDelta: -5,
      outcome: `The Lightning payment wouldn't route — the channel lacked capacity. The sale fell through and ${customer.name} got frustrated.`,
    };
  }

  // Card sales can be reversed later.
  let chargeback = 0;
  if (method === "card" && cardChargesBack(customer)) {
    chargeback = price; // whole sale reversed
  }

  const net = round2(price - fee - chargeback);
  const reputationDelta = computeReputation(customer, method, matchesPref, spiked, chargeback > 0);
  const outcome = buildOutcome(customer, method, fee, spiked, chargeback > 0);
  // A conversion: the customer preferred something else, but paid in BCH.
  const converted = method === "bch" && customer.prefers !== "bch";

  return {
    ...base,
    method,
    completed: true,
    grossUsd: round2(price),
    feeUsd: fee,
    feeSpiked: spiked,
    chargebackUsd: round2(chargeback),
    netUsd: net,
    reputationDelta,
    converted,
    outcome,
  };
}

function computeReputation(
  customer: Customer,
  method: AcceptMethod,
  matchesPref: boolean,
  spiked: boolean,
  chargedBack: boolean,
): number {
  let rep = 3; // a completed sale is a happy customer by default

  if (!matchesPref) {
    // You steered them off their preference. How much they mind depends on
    // flexibility — but everyone is charmed when you show them BCH.
    if (method === "bch") {
      rep += 1; // "wow, that was instant and free" — a delightful conversion
    } else {
      const flex = flexibilityOf(customer);
      rep -= flex === "firm" ? 3 : 1; // firm customers grumble more
    }
  } else if (method === "bch") {
    rep += 1; // fast + free leaves a great impression
  }

  if (spiked) rep -= 1; // customer notices the awkward on-chain fee
  if (chargedBack) rep -= 3; // dispute hassle sours the relationship
  return rep;
}

function buildOutcome(
  customer: Customer,
  method: AcceptMethod,
  fee: number,
  spiked: boolean,
  chargedBack: boolean,
): string {
  const label = methodLabel(method);
  if (chargedBack) {
    return `You took ${label} from ${customer.name}. Weeks later it's disputed — the whole ${formatUsd(customer.priceUsd)} is clawed back, plus you already ate the processor fee.`;
  }

  const steered = isSteer(customer, method);

  // The educational payoff: converting someone to BCH.
  if (steered && method === "bch") {
    return `You suggest BCH. ${customer.name} scans the QR — it clears instantly for a fraction of a cent. "That's it? That was way easier than ${methodLabel(
      customer.prefers,
    ).toLowerCase()}!" A new BCH convert, and you kept essentially the whole ${formatUsd(customer.priceUsd)}.`;
  }

  const grumble =
    steered && flexibilityOf(customer) === "firm"
      ? ` ${customer.name} would've preferred ${methodLabel(customer.prefers)} and isn't thrilled about the switch.`
      : "";

  switch (method) {
    case "bch":
      return `${customer.name} pays in BCH. It clears instantly for a fraction of a cent — you keep essentially the whole ${formatUsd(customer.priceUsd)}.`;
    case "btc-lightning":
      return `${customer.name} zaps it over Lightning. Fast and cheap — a tiny fee, no waiting.${grumble}`;
    case "btc-onchain":
      return (
        (spiked
          ? `${customer.name} pays BTC on-chain — but the network's congested, so ${formatUsd(fee)} of this sale vanishes into fees.`
          : `${customer.name} pays BTC on-chain. It settles, minus a ${formatUsd(fee)} network fee.`) + grumble
      );
    case "card":
      return `${customer.name} taps a card. It works, but the processor skims ${formatUsd(fee)} — and card sales can still be disputed later.${grumble}`;
    case "decline":
      return "";
  }
}

/** An empty running result. */
export function emptyHustleResult(): HustleResult {
  return {
    rounds: 0,
    completedSales: 0,
    declined: 0,
    grossUsd: 0,
    feesUsd: 0,
    spikeFeesUsd: 0,
    chargebacksUsd: 0,
    netUsd: 0,
    reputation: STARTING_REPUTATION,
    bchConverts: 0,
    score: 0,
    sales: [],
  };
}

/** Fold a resolved sale into the running result (returns a new object). */
export function applySale(result: HustleResult, sale: HustleSale): HustleResult {
  const next: HustleResult = { ...result, sales: [...result.sales, sale] };
  next.rounds += 1;
  if (sale.completed) {
    next.completedSales += 1;
    next.grossUsd = round2(next.grossUsd + sale.grossUsd);
    next.feesUsd = round2(next.feesUsd + sale.feeUsd);
    if (sale.feeSpiked) next.spikeFeesUsd = round2(next.spikeFeesUsd + sale.feeUsd);
    next.chargebacksUsd = round2(next.chargebacksUsd + sale.chargebackUsd);
    next.netUsd = round2(next.netUsd + sale.netUsd);
    if (sale.converted) next.bchConverts += 1;
  } else if (sale.method === "decline") {
    next.declined += 1;
  }
  next.reputation = clamp(next.reputation + sale.reputationDelta, 0, 100);
  return next;
}

/** Finalize: compute the session score. */
export function finalizeHustle(result: HustleResult): HustleResult {
  // Total potential gross if every sale had completed (declines + fails count
  // against you as missed revenue).
  const potential = result.sales.reduce((s, x) => s + x.priceUsd, 0) || 1;
  // How much of the possible revenue you actually kept after fees/chargebacks.
  const capture = clamp((result.netUsd / potential) * 100, 0, 100);
  const repScore = result.reputation; // 0–100
  // Small bonus for spreading BCH — the whole point of the shop.
  const convertBonus = clamp(result.bchConverts * 2, 0, 12);
  const score = Math.round(capture * 0.6 + repScore * 0.4 + convertBonus);
  return { ...result, score: clamp(score, 0, 100) };
}

/**
 * What WOULD the net have been if every completed sale had been taken in BCH
 * instead? Used to show the merchant how much fees + chargebacks cost them.
 */
export function bchCounterfactualNet(result: HustleResult): number {
  let net = 0;
  for (const sale of result.sales) {
    if (sale.method === "decline") continue; // declines are missed either way
    // In BCH, the sale completes (no routing failure, no chargeback) with a
    // near-zero fee.
    net += sale.priceUsd - BCH_ACCEPT_FEE_USD;
  }
  return round2(net);
}

export function formatUsd(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

export function formatFee(usd: number): string {
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return "<$0.01";
  return `$${usd.toFixed(2)}`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}
